import { getAiAgentConfig } from '@/api/ai-chat';
import { DEPLOY_CLOUD, DEPLOY_ENV } from '@/constant/constant';
import { DCL_EXTRANET, DCL_INTRANET } from '@/def/domain';
import store from '@/store';
import ls from '@/util/ls';

const lsDevConfKey = 'test-one-dev-conf';

interface DevConf {
  enable: boolean;
  ruleMap?: DevRuleMap;
}

export interface DevRuleMap {
  [app: string]: DevRule | undefined;
}

export interface DevRule {
  name: string;
  entry: string;
  entryType: 'js' | 'html';
  withProduct: boolean;
}

function getDevConf(): DevConf {
  let conf: DevConf = ls.get(lsDevConfKey);

  if (!conf) {
    conf = {
      enable: false,
      ruleMap: {},
    };
  }

  return conf;
}

export function isDevMode(): boolean {
  return getDevConf().enable;
}

/** 是否海外测试环境 */
export const isOverseaTestEnv = (): boolean => {
  const { origin } = window.location;
  // 当前为海外测试环境
  if (origin.includes(DCL_INTRANET.overseaDevelopment)) {
    return true;
  }
  return false;
};

/** 是否海外正式环境 */
export const isOverseaFormal = (): boolean => {
  const { origin } = window.location;
  // 当前为海外正式环境
  if (origin.includes(DCL_INTRANET.overseaProduction)) {
    return true;
  }
  return false;
};

/**
 * 是否为测试环境
 * @returns {boolean}
 */
export const isTestEnv = (): boolean => {
  const { origin } = window.location;
  // 当前为内网正式环境
  if (origin.includes(DCL_INTRANET.production)) {
    return false;
  }
  // 当前为内网正式环境(https)
  if (origin.includes(DCL_INTRANET.productionHttps)) {
    return false;
  }
  // 当前为外网正式环境
  if (origin.includes(DCL_EXTRANET.production)) {
    return false;
  }
  // 当前为海外正式环境
  if (origin.includes(DCL_INTRANET.overseaProduction)) {
    return false;
  }
  return true;
};

/**
 * 是否为内网
 * @returns
 */
export const isIntranet = (): boolean => {
  const { origin } = window.location;
  // 外网测试环境域名 -> 外网
  if (origin.includes(DCL_EXTRANET.development)) {
    return false;
  }
  // 外网正式环境域名 -> 外网
  if (origin.includes(DCL_EXTRANET.production)) {
    return false;
  }
  // 云上部署环境，视为外网
  if (DEPLOY_ENV === DEPLOY_CLOUD) {
    return false;
  }
  return true;
};

export const USER_IMG_EXTRANET = 'https://dayu.woa.com';

/**
 * 是否打开后台调试
 * @returns {boolean}
 */
export const isDebugBackend = (): boolean => {
  const { origin } = window.location;
  // 只有 http://test.aisee.woa.com 和 本地 才打开后台调试
  if (
    origin.includes(DCL_INTRANET.development) ||
    origin.includes('localhost')
  ) {
    return true;
  }
  return false;
};

let aiAgentConfigTask: Promise<void> | null = null;

/** 正式环境拉一次白名单，写进 store。测试环境不请求。重复调用共用同一次请求。 */
export const loadAiAgentConfig = (): void => {
  if (isTestEnv() || aiAgentConfigTask || store.aiAgentAppIds) {
    return;
  }
  aiAgentConfigTask = getAiAgentConfig()
    .then((config) => {
      store.setAiAgentConfig(config?.app_ids ?? [], config?.user_ids ?? []);
    })
    .catch(() => {
      store.setAiAgentConfig([], []);
    });
};

/**
 * 是否允许使用 AI 助手。只读，不发请求。
 * 测试环境直接允许。正式环境配置未返回、未命中或失败时为 false。
 */
export const allowAiAgent = (): boolean => {
  if (isTestEnv()) {
    return true;
  }
  const appIds = store.aiAgentAppIds;
  const userIds = store.aiAgentUserIds;
  if (!appIds || !userIds) {
    return false;
  }
  const userId = store.user || store.userInfo?.user_info.user_id || '';
  const appAllowed = Boolean(store.appId) && appIds.includes(store.appId);
  const userAllowed = Boolean(userId) && userIds.includes(userId);
  return appAllowed || userAllowed;
};
